/**
 * The full shop list, for admin pickers — every shop, not just the caller's own.
 *
 * Separate from `myShops` in `catalogue.ts` on purpose: that one needs no permission and answers
 * "where can I sell", this one needs `shop.view` and answers "which shop does this user belong
 * to", which only an owner assigning users, prices or stock ever asks.
 */

import { api } from '../api.js';
import { shopListTag, shopTag } from '../tags.js';
import type { Shop, ShopId } from '../../types/api.js';

export type CreateShopBody = {
  readonly code: string;
  readonly name: string;
  readonly location?: string | undefined;
  readonly phone?: string | undefined;
  readonly kraPin?: string | undefined;
  /** One of the curated looks. Omitted means the default — the server decides, not this file. */
  readonly themeCode?: string | undefined;
};

export type UpdateShopBody = {
  readonly name: string;
  readonly location?: string | undefined;
  readonly phone?: string | undefined;
  readonly kraPin?: string | undefined;
  /** Omitted leaves the shop's look alone; it never resets to the default. */
  readonly themeCode?: string | undefined;
};

export type UpdateShopReceiptBody = {
  readonly header?: string | undefined;
  readonly footer?: string | undefined;
  readonly paperWidthMm: number;
};

export const shopsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listShops: build.query<Shop[], { includeInactive?: boolean | undefined } | void>({
      query: (params) => ({ url: '/shops', params: params ?? {} }),
      providesTags: (result) => [shopListTag(), ...(result ?? []).map((shop) => shopTag(shop.id))],
    }),

    /**
     * The far ends a transfer can go to: id and name, nothing else. Readable with
     * `transfer.create`, which a manager holds — unlike `listShops`, which carries every shop's
     * phone, KRA PIN and receipt text and needs `shop.view`, which a manager does not.
     */
    listTransferPartners: build.query<{ id: ShopId; name: string }[], void>({
      query: () => '/shops/partners',
      providesTags: [shopListTag()],
    }),

    /** Code is never editable after creation — it appears in issued document numbers. */
    createShop: build.mutation<ShopId, CreateShopBody>({
      query: (body) => ({ url: '/shops', method: 'POST', body }),
      invalidatesTags: [shopListTag()],
    }),

    updateShop: build.mutation<void, { id: ShopId; body: UpdateShopBody }>({
      query: ({ id, body }) => ({ url: `/shops/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [shopTag(id), shopListTag()],
    }),

    /** Paper width lives here so a printer upgrade is one row, not a deployment. */
    updateShopReceipt: build.mutation<void, { id: ShopId; body: UpdateShopReceiptBody }>({
      query: ({ id, body }) => ({ url: `/shops/${id}/receipt`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [shopTag(id), shopListTag()],
    }),

    setShopActive: build.mutation<void, { id: ShopId; isActive: boolean }>({
      query: ({ id, isActive }) => ({
        url: `/shops/${id}/activation`,
        method: 'POST',
        body: { isActive },
      }),
      invalidatesTags: (_result, _error, { id }) => [shopTag(id), shopListTag()],
    }),
  }),
});

export const {
  useListShopsQuery,
  useListTransferPartnersQuery,
  useCreateShopMutation,
  useUpdateShopMutation,
  useUpdateShopReceiptMutation,
  useSetShopActiveMutation,
} = shopsApi;
